/**
 * Menú de sesión del encabezado.
 *
 * Muestra quién está conectado y su rol, deja registrar la huella del
 * dispositivo para no reescribir la contraseña cada vez, y cierra la sesión.
 *
 * Sin Firebase configurado la app corre abierta: el menú lo dice con todas
 * sus letras y «Salir» sólo bloquea el equipo, que no es lo mismo que
 * autenticar.
 */

import { useEffect, useRef, useState } from 'react';
import { Fingerprint, LogOut, Monitor, Moon, Sun, User } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useDialogo } from '@/components/ui/dialogo';
import { useAuth, NOMBRE_ROL } from '@/hooks/useAuth';
import { useBiometria } from '@/hooks/useBiometria';

type Tema = 'oscuro' | 'claro' | 'sistema';
const CLAVE_TEMA = 'xtreme:tema';

function aplicarTema(t: Tema) {
  const raiz = document.documentElement;
  if (t === 'sistema') raiz.removeAttribute('data-theme');
  else raiz.setAttribute('data-theme', t === 'oscuro' ? 'dark' : 'light');
}

export function MenuSesion({ alBloquear }: { alBloquear: () => void }) {
  const [abierto, setAbierto] = useState(false);
  const [tema, setTema] = useState<Tema>('oscuro');
  const [avisoBio, setAvisoBio] = useState<string | null>(null);
  const caja = useRef<HTMLDivElement>(null);

  const { confirmar } = useDialogo();
  const { modoLocal, perfil, salir } = useAuth();
  const bio = useBiometria(perfil?.email ?? null);

  useEffect(() => {
    const guardado = (localStorage.getItem(CLAVE_TEMA) as Tema | null) ?? 'oscuro';
    setTema(guardado);
    aplicarTema(guardado);
  }, []);

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', tecla);
    };
  }, [abierto]);

  function cambiarTema(t: Tema) {
    setTema(t);
    aplicarTema(t);
    try {
      localStorage.setItem(CLAVE_TEMA, t);
    } catch {
      // Sin espacio: al menos cambia en esta sesión.
    }
  }

  async function alternarHuella() {
    setAvisoBio(null);
    try {
      if (bio.registrada) {
        bio.olvidar();
        setAvisoBio('Huella quitada de este dispositivo.');
        return;
      }
      await bio.registrar();
      setAvisoBio('Listo: la próxima vez puedes entrar con la huella.');
    } catch (e) {
      setAvisoBio(e instanceof Error ? e.message : 'No se pudo registrar la huella.');
    }
  }

  async function cerrar() {
    setAbierto(false);
    const si = await confirmar({
      titulo: modoLocal ? 'Bloquear la aplicación' : 'Cerrar la sesión',
      mensaje: modoLocal
        ? 'Se vuelve a la pantalla de inicio. Las guías cargadas quedan guardadas en este dispositivo, no se pierde nada.'
        : 'Se cierra la sesión en este dispositivo. Las guías ya sincronizadas quedan en la base; las pendientes se envían al volver a entrar.',
      aceptar: modoLocal ? 'Bloquear' : 'Salir',
    });
    if (!si) return;

    if (!modoLocal) {
      await salir();
      // La huella queda asociada a un correo: si cambia de usuario, sobra.
      if (bio.registrada && !bio.registradaPara(perfil?.email ?? null)) bio.olvidar();
    }
    alBloquear();
  }

  const TEMAS: { id: Tema; texto: string; icono: typeof Sun }[] = [
    { id: 'oscuro', texto: 'Oscuro', icono: Moon },
    { id: 'claro', texto: 'Claro', icono: Sun },
    { id: 'sistema', texto: 'Sistema', icono: Monitor },
  ];

  const iniciales = (perfil?.nombre ?? perfil?.email ?? '')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  return (
    <div className="relative" ref={caja}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-label="Menú de sesión"
        className={cn(
          'grid h-9 min-w-9 place-items-center rounded-full border border-linea px-2 text-xs font-semibold transition-colors',
          abierto ? 'bg-petroleo-500 text-texto' : 'text-texto-mute hover:bg-white/5 hover:text-texto',
        )}
      >
        {iniciales || <User size={16} />}
      </button>

      {abierto && (
        <div
          role="menu"
          className="metal absolute right-0 z-40 mt-2 w-60 overflow-hidden rounded-tarjeta border border-linea shadow-tarjeta"
        >
          <div className="border-b border-linea px-3 py-2.5">
            {perfil ? (
              <>
                <div className="truncate text-sm font-semibold">{perfil.nombre || perfil.email}</div>
                <div className="truncate text-[11px] text-texto-mute">{perfil.email}</div>
                <div className="mt-1 inline-block rounded bg-petroleo-800/70 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-petroleo-300">
                  {NOMBRE_ROL[perfil.rol]}
                </div>
              </>
            ) : (
              <>
                <div className="text-sm font-semibold">Sesión local</div>
                <div className="text-[11px] text-fluor-amarillo">Sin autenticación todavía</div>
              </>
            )}
          </div>

          {/* Huella: sólo tiene sentido con sesión real y dispositivo compatible */}
          {!modoLocal && bio.soportado && (
            <div className="border-b border-linea px-3 py-2">
              <button
                type="button"
                onClick={() => void alternarHuella()}
                disabled={bio.ocupado}
                className="flex w-full items-center gap-2 rounded-control px-1 py-1.5 text-xs text-texto-mute transition-colors hover:bg-white/5 hover:text-texto disabled:opacity-50"
              >
                <Fingerprint size={14} className={bio.registrada ? 'text-fluor-verde' : ''} />
                {bio.registrada ? 'Quitar la huella de este equipo' : 'Entrar con huella en este equipo'}
              </button>
              {avisoBio && <p className="mt-1 px-1 text-[11px] text-texto-mute">{avisoBio}</p>}
            </div>
          )}

          <div className="border-b border-linea px-3 py-2">
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-texto-mute">
              Apariencia
            </div>
            <div className="flex gap-1">
              {TEMAS.map(({ id, texto, icono: Icono }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => cambiarTema(id)}
                  title={texto}
                  aria-pressed={tema === id}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1 rounded-control py-1.5 text-[11px] transition-colors',
                    tema === id
                      ? 'bg-petroleo-500 text-texto'
                      : 'text-texto-mute hover:bg-white/5 hover:text-texto',
                  )}
                >
                  <Icono size={12} />
                  {texto}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            role="menuitem"
            onClick={() => void cerrar()}
            className="flex w-full items-center gap-2 px-3 py-2.5 text-sm text-fluor-rojo transition-colors hover:bg-fluor-rojo/10"
          >
            <LogOut size={15} />
            {modoLocal ? 'Bloquear' : 'Salir'}
          </button>
        </div>
      )}
    </div>
  );
}
